import React from 'react';
import { Link } from 'react-router-dom';
import { APP_STORE_URL, PLAY_STORE_URL, SUPPORT_EMAIL } from '../../constants/links';

const EXPLORE = [
    { href: '#analyse', label: 'Real meals' },
    { href: '#how', label: 'How it works' },
    { href: '#features', label: 'Features' },
    { href: '#screens', label: 'Screens' },
    { href: '#about', label: 'About' },
    { href: '#faq', label: 'FAQ' },
];

const linkClass = 'text-[15px] text-dark-textSecondary transition-colors hover:text-white';
const headingClass =
    'mb-4 text-[10px] font-medium uppercase tracking-[0.14em] text-dark-textSubtle';

const Footer: React.FC = () => {
    const year = new Date().getFullYear();

    return (
        <footer className="relative z-[1] border-t border-dark-gunmetal bg-[#080A0B]">
            <div className="mx-auto grid max-w-[1180px] gap-[clamp(28px,4vw,56px)] px-5 pt-[clamp(44px,6vw,72px)] sm:grid-cols-2 lg:grid-cols-4">
                <div className="min-w-0">
                    <img
                        src="/assets/fork-logo.png"
                        alt="FORK."
                        className="h-6 w-auto object-contain"
                    />
                    <p className="mt-2.5 text-[11px] font-medium uppercase tracking-[0.14em] text-dark-textSubtle">
                        AI nutritionist
                    </p>
                    <p className="mt-5 max-w-[34ch] text-[15px] leading-[1.65] text-dark-textSecondary">
                        We build tools that turn everyday meal photos into clear nutrition insights so
                        you can focus on eating well without the busywork.
                    </p>
                </div>

                <nav className="min-w-0">
                    <h4 className={headingClass}>Explore</h4>
                    <div className="grid gap-[11px]">
                        {EXPLORE.map((link) => (
                            <a key={link.href} href={link.href} className={linkClass}>
                                {link.label}
                            </a>
                        ))}
                    </div>
                </nav>

                <nav className="min-w-0">
                    <h4 className={headingClass}>Legal &amp; support</h4>
                    <div className="grid gap-[11px]">
                        <Link to="/privacy" className={linkClass}>
                            Privacy policy
                        </Link>
                        <Link to="/terms" className={linkClass}>
                            Terms &amp; conditions
                        </Link>
                        <a href={`mailto:${SUPPORT_EMAIL}`} className={linkClass}>
                            {SUPPORT_EMAIL}
                        </a>
                    </div>
                </nav>

                <div className="min-w-0">
                    <h4 className={headingClass}>Get the app</h4>
                    <div className="grid gap-[11px]">
                        <a
                            href={APP_STORE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`inline-flex items-center gap-[9px] ${linkClass}`}
                        >
                            <svg
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                className="shrink-0"
                                aria-hidden="true"
                            >
                                <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
                            </svg>
                            App Store
                        </a>
                        <a
                            href={PLAY_STORE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`inline-flex items-center gap-[9px] ${linkClass}`}
                        >
                            <svg
                                width="14"
                                height="15"
                                viewBox="0 0 24 24"
                                className="shrink-0"
                                aria-hidden="true"
                            >
                                <path
                                    d="M3.6 1.84C3.24 2.09 3 2.5 3 3.05v17.9c0 .55.24.96.6 1.21l9.53-10.16L3.6 1.84z"
                                    fill="#00D0FF"
                                />
                                <path
                                    d="M17.1 8.28L5.42 1.66C5 1.42 4.6 1.4 4.28 1.6l9.53 10.16 3.29-3.48z"
                                    fill="#00E47B"
                                />
                                <path
                                    d="M20.9 10.44l-3.8-2.16-3.29 3.48 3.29 3.48 3.8-2.15c.9-.51.9-2.14 0-2.65z"
                                    fill="#FFCE00"
                                />
                                <path
                                    d="M4.28 22.4c.32.2.72.18 1.14-.06l11.68-6.62-3.29-3.48L4.28 22.4z"
                                    fill="#FF3A44"
                                />
                            </svg>
                            Google Play
                        </a>
                    </div>
                </div>
            </div>

            <div className="mx-auto mt-[clamp(32px,4vw,52px)] flex max-w-[1180px] flex-wrap items-center justify-between gap-3 border-t border-dark-gunmetal px-5 pb-[30px] pt-[22px]">
                <p className="text-[13px] text-dark-textSubtle">
                    © {year} Fork by InfiniteLoop Technologies. All rights reserved.
                </p>
                <p className="text-[13px] text-dark-textSubtle">Built to make nutrition simpler.</p>
            </div>
        </footer>
    );
};

export default Footer;
